import * as React from 'react'
import { AnchorsOfForms } from '@constants/forms'
import { AdvCake } from '@features/advcakeModule'
import { apiUrls } from '@http/api-urls'
import { httpService } from '@http/service'
import { TextField } from '@shared/components/text-field'
import { useSmsRetry } from '@shared/hooks/use-sms-retry'
import scrollToElement from '@shared/pipes/scroll'
import cx from 'clsx'
import { Form, FormikProvider, useFormik } from 'formik'
import moment from 'moment/moment'

import { ApplicationFormContext } from '../../Application'
import { sendSmsCode } from '../../steps/Primary/api'
import { transformFormToDto } from '../../transform.dto'
import { Submit } from '../Submit'

import styles from './ConfirmSms.module.scss'

interface Props {
	phone: string
	changePhone?: () => void
	title?: string
	extraText?: string
	leadProductType?: string
}

type FormStatuses =
	| 'pending-confirmation'
	| 'confirmed'
	| 'lead-is-created'
	| 'wrong-code'
	| 'created-lead-error'

export const ConfirmSms = React.memo(
	({
		phone,
		title = 'Подтвердите заявку кодом из смс',
		extraText = 'Введите его, чтобы мы смогли рассмотреть заявку',
		changePhone,
		leadProductType,
	}: Props) => {
		const context = React.useContext(ApplicationFormContext)

		const formik = useFormik({
			initialValues: {
				code: '',
			},
			initialStatus: 'pending-confirmation' as FormStatuses,
			onSubmit: async ({ code }, { setStatus }) => {
				const formStatus: FormStatuses = formik.status

				try {
					if (formStatus === 'pending-confirmation' || formStatus === 'wrong-code') {
						if (context.hasEsia) {
							const data = await httpService.post<Record<string, any>>(
								apiUrls.esia.verifySMSCode(
									code,
									Object.entries(context.optionalDocsIds)
										.filter(([, value]) => value)
										.map(([id]) => id)
								)
							)

							if (data?.verified === false) throw { response: { status: 401 } }
						} else {
							const data = await httpService.post<{ verified: boolean }>(apiUrls.sms.confirm(code))

							if (data?.verified === false) throw { response: { status: 401 } }
						}

						setStatus('confirmed' as FormStatuses)
					}

					if (!context.hasLead) {
						const payload = transformFormToDto(context.formController.values)
						const advCakeId = `${payload.participants[0].phone}-${moment(new Date()).format(
							'DD.MM.YYYY-HH:mm:ss'
						)}`
						await httpService.post(apiUrls.lead.create(), {
							...payload,
							marketingData: {
								...payload.marketingData,
								advCakeId,
							},
						})
						AdvCake.pushEvent({
							pageType: 6,
							user: {
								email: '', // email в виде хеша или уникальный идентификатор, данное поле можно оставить пустым
								type: '', // old, new - опционально, передавать пустое значение в кавычках (''), если тип пользователя неизвестен
							},
							leadInfo: {
								id: advCakeId, //Передаем поле **ID** созданного Лида
								leadid: '', // ID продукта(равно id из пункта 3), если такой есть, если нет, оставляем пустым
								name: leadProductType, //наименование услуги лида
								totalPrice: 0, //Константа. Всегда равно 0
								coupon: '', // Промокод. Если механики промокодов нет - пустое поле
							},
						})
					}

					if (context.hasEsia) {
						context.setFormCookie('completed')
						context.setHasConfirmed(true)
					}

					context.formController.refetch(() => {
						scrollToElement(`#${AnchorsOfForms.application}`, { time: 0 })
					})
				} catch (error: any) {
					console.dir(error)
					if (error?.response?.status === 401) {
						setStatus('wrong-code' as FormStatuses)
					} else {
						setStatus('created-lead-error' as FormStatuses)
					}
				}
			},
		})

		const formStatus: FormStatuses = formik.status

		const onSendSms = React.useCallback(async () => {
			formik.setSubmitting(true)

			try {
				await sendSmsCode(phone)
			} catch (error: any) {
				console.dir(error)
				if (error?.response?.status === 401) {
					formik.setStatus('wrong-code' as FormStatuses)
				} else {
					formik.setStatus('created-lead-error' as FormStatuses)
				}
			} finally {
				formik.setSubmitting(false)
			}
		}, [])

		const { isRunning, text, retry } = useSmsRetry(onSendSms)

		const codeProps: React.ComponentProps<typeof TextField> = {
			mask: '9999',
			maskChar: '',
			required: true,
			onChange: formik.handleChange,
			value: formik.values.code,
			className: styles.input,
			inputMode: 'numeric',
			error: formStatus === 'wrong-code' && '',
			name: 'code',
			label: 'Код из смс',
			disabled: formik.isSubmitting,
		}

		return (
			<FormikProvider value={formik}>
				<Form autoComplete='off'>
					<h4 className={styles.title}>{title}</h4>
					<p className={cx(styles.text)}>
						Мы отправили на номер {phone}&nbsp;код&nbsp;подтверждения.
					</p>
					<p className={cx(styles.text)}>{extraText}</p>
					<div className={styles.wrap}>
						<TextField {...codeProps} />
						<div
							className={cx(styles.bottom, {
								[styles.smallBottom]: !changePhone,
							})}
						>
							<span
								className={cx(styles.linkBtn, {
									[styles.disabled]: isRunning || formik.isSubmitting,
								})}
								onClick={!isRunning ? retry : undefined}
							>
								{text}
							</span>

							{changePhone && (
								<span
									className={cx(styles.linkBtn, { [styles.disabled]: formik.isSubmitting })}
									onClick={changePhone}
								>
									Изменить номер телефона
								</span>
							)}
						</div>
						{formStatus === 'wrong-code' && (
							<span className={styles.error}>Неправильный код подтверждения</span>
						)}
						{formStatus === 'created-lead-error' && (
							<span className={styles.error}>Произошла ошибка, попробуйте ещё раз</span>
						)}
					</div>
					<Submit
						classNameBtn={styles.classNameBtn}
						disabled={formik.isSubmitting}
						text='Отправить'
					/>
				</Form>
			</FormikProvider>
		)
	}
)
